import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminApi,
  healthApi,
  guidelinesApi,
  HealthResponse,
  User,
} from "../../api";
import { celeryApi, CeleryStats } from "../../api/celery";
import { useAuth } from "../../context/AuthContext";
import { tokenStore } from "../../utils/tokenStore";
import DashboardPage from "./DashboardPage";
import { ORCHESTRATOR_API_URL } from "../../api/endpoints";

interface ServiceHealth {
  up: number;
  down: number;
  total: number;
}

interface DashboardStats {
  health: HealthResponse | null;
  serviceHealth: ServiceHealth;
  guidelinesCount: number;
  usersCount: number;
  adminUsersCount: number;
  demoUsersCount: number;
  celery: CeleryStats | null;
}

const DashboardContainer: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    health: null,
    serviceHealth: { up: 0, down: 0, total: 0 },
    guidelinesCount: 0,
    usersCount: 0,
    adminUsersCount: 0,
    demoUsersCount: 0,
    celery: null,
  });
  const [loading, setLoading] = useState(true);

  const getToken = (): string | null => tokenStore.get();

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const [
        healthRes,
        svcHealthRes,
        guidelinesRes,
        celeryRes,
        usersRes,
      ] = await Promise.allSettled([
        healthApi.check(),
        adminApi.get<{ services: { status: string }[] }>(
          "/admin/health-check"
        ),
        guidelinesApi.list({ limit: 1, offset: 0 }),
        celeryApi.getStats(),
        (async () => {
          const response = await fetch(`${ORCHESTRATOR_API_URL}/api/v1/users`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (!response.ok) {
            throw new Error("Failed to fetch users");
          }
          return (await response.json()) as User[];
        })(),
      ]);

      let serviceHealth: ServiceHealth = { up: 0, down: 0, total: 0 };
      if (svcHealthRes.status === "fulfilled") {
        const svcs = svcHealthRes.value.data.services || [];
        const up = svcs.filter((s) => s.status === "up").length;
        serviceHealth = { up, down: svcs.length - up, total: svcs.length };
      }

      const users = usersRes.status === "fulfilled" ? usersRes.value : [];
      const adminUsersCount = users.filter(
        (u) => u.role === "superadmin" || u.is_superuser
      ).length;
      const demoUsersCount = users.length - adminUsersCount;

      setStats({
        health:
          healthRes.status === "fulfilled" ? healthRes.value.data : null,
        serviceHealth,
        guidelinesCount:
          guidelinesRes.status === "fulfilled"
            ? guidelinesRes.value.data.total
            : 0,
        usersCount: users.length,
        adminUsersCount,
        demoUsersCount,
        celery:
          celeryRes.status === "fulfilled" ? celeryRes.value.data : null,
      });
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <DashboardPage
      stats={stats}
      loading={loading}
      onRefresh={fetchStats}
      onNavigate={navigate}
      user={user}
    />
  );
};

export default DashboardContainer;
